import { currencyMeta, currencyName } from "../currencies/meta";
import { formatDate, type AppLocale } from "../i18n/format";
import type { Coverage } from "../rates/coverage";
import { coverageCopy } from "../rates/coverageCopy";
import { Sheet } from "./Sheet";
import { StarIcon, TrashIcon } from "./Icons";

/**
 * What the three-dot button opens on a quote row.
 *
 * DELIBERATELY THREE THINGS, and every one of them is a capability the app
 * already has:
 *
 *   make base   also reachable by tapping the row
 *   remove      also reachable by swiping the row
 *   the facts   rate, date, source, peg, coverage — the only genuinely new
 *               surface, and CLAUDE.md asks for it by name ("Note pegs in the
 *               currency info panel")
 *
 * The two actions are here because a gesture must never be the ONLY way to do
 * something: swipe-to-delete is invisible to a keyboard, to a screen reader,
 * and to anyone who has not been told it exists. This sheet is the discoverable
 * path, not a second feature.
 */

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly locale: AppLocale;
  readonly code: string;
  /** "1 SAR = 0.2667 USD", already formatted by the caller. */
  readonly unitRateText: string;
  /** ISO date of this currency's rate. */
  readonly asOf: string | null;
  readonly source: string;
  readonly coverage: Coverage;
  readonly onMakeBase: () => void;
  readonly onRemove: () => void;
}

export function RowSheet({
  open,
  onClose,
  locale,
  code,
  unitRateText,
  asOf,
  source,
  coverage,
  onMakeBase,
  onRemove,
}: Props) {
  const ar = locale === "ar";
  const meta = currencyMeta(code);
  const name = currencyName(code, locale);
  const note = coverageCopy(coverage, locale);

  /* Each action closes the sheet itself rather than leaving that to the caller:
     a sheet that stays open over the thing it just changed is a bug every time. */
  const run = (action: () => void) => () => {
    action();
    onClose();
  };

  /** `iso` is set only on facts that are dates, so they render as <time>. */
  const facts: {
    readonly label: string;
    readonly value: string;
    readonly iso?: string;
  }[] = [];

  /*
   * WITHHOLDING FOLLOWS THE CURRENCY, NOT THE SCREEN.
   *
   * This is the second place the same leak appeared. The row was fixed to
   * suppress both its amount and its unit-rate line, and the sheet then went on
   * stating "Rate: 1 SAR = 32.5600 SYP" one tap away — the identical figure,
   * from the identical rate, in the identical app. A refusal that holds only on
   * the surface you happened to check is not a refusal. Every surface that can
   * show a number has to ask.
   */
  if (coverage.state !== "withheld") {
    facts.push({ label: ar ? "السعر" : "Rate", value: unitRateText });
  }

  if (asOf) {
    facts.push({
      label: ar ? "بتاريخ" : "As of",
      value: formatDate(asOf, locale),
      iso: asOf,
    });
  }

  facts.push({ label: ar ? "المصدر" : "Source", value: source });

  if (meta.pegged) {
    /*
     * Stated as policy, not as a rate we happen to observe. A peg is exact by
     * decree until it breaks — and coverage.ts already checks whether this one
     * still holds, so the claim here is never older than the data.
     */
    facts.push({
      label: ar ? "مربوطة" : "Pegged",
      value: `1 ${meta.pegged.to} = ${meta.pegged.rate} ${code}`,
    });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      locale={locale}
      title={name}
      subtitle={code}
    >
      {note ? (
        <p className="sheet__note" data-coverage={coverage.state}>
          {note.text}
        </p>
      ) : null}

      <dl className="sheet__facts">
        {facts.map((fact) => (
          <div className="sheet__fact" key={fact.label}>
            <dt className="sheet__fact-label">{fact.label}</dt>
            {/*
             * dir="auto", NOT dir="ltr".
             *
             * Forcing LTR is right for "1 SAR = 0.9793 AED" and wrong for
             * "09 أغسطس 2026": three runs, two Latin and one Arabic, which the
             * bidi algorithm then laid out as day, YEAR, month. It looked
             * plausible enough that only measuring the painted x positions
             * caught it. `auto` lets the first strong character decide, so a
             * rate stays LTR and a localised date reads in its own direction
             * with each numeral still isolated.
             */}
            <dd className="sheet__fact-value" dir="auto">
              {fact.iso ? <time dateTime={fact.iso}>{fact.value}</time> : fact.value}
            </dd>
          </div>
        ))}
      </dl>

      <div className="sheet__actions">
        <button type="button" className="sheet__action" onClick={run(onMakeBase)}>
          <StarIcon />
          <span>{ar ? "اجعلها العملة الأساسية" : "Make this the base currency"}</span>
        </button>

        <button
          type="button"
          className="sheet__action sheet__action--destructive"
          onClick={run(onRemove)}
        >
          <TrashIcon />
          <span>{ar ? `إزالة ${name}` : `Remove ${name}`}</span>
        </button>
      </div>
    </Sheet>
  );
}
